<template>
	<div id="provision">
		<Header :title="strings.provision.title">{{
			strings.provision.header
		}}</Header>

		<v-card :loading="loading">
			<v-stepper v-model="index" vertical>
				<template v-for="(step, i) in steps">
					<v-stepper-step
						:key="`step-${i}`"
						:step="i + 1"
						:complete="index > i + 1"
						:rules="[() => !step.error || step.error]"
					>
						{{ step.title }}
						<small v-if="step.summary">{{ step.summary }}</small>
					</v-stepper-step>

					<v-stepper-content :key="`content-${i}`" :step="i + 1">
						<v-alert :value="!!step.error" color="rust-red" text>
							<span class="body-2">{{ step.error }}</span>
						</v-alert>

						<v-btn
							v-if="!!step.error"
							color="ocean"
							outlined
							small
							@click="performStep(index)"
							>Retry</v-btn
						>
					</v-stepper-content>
				</template>
			</v-stepper>
		</v-card>

		<NextFab :value="finished" :next="nextFn">
			<template #tooltip>{{ strings.provision.complete }}</template>
		</NextFab>
	</div>
</template>

<style lang="scss" scoped>
#provision {
	margin-bottom: 68px;
}
</style>

<script>
import { mapActions } from "vuex";

import strings from "@/utils/strings";

import Header from "../shared/Header";
import NextFab from "../shared/NextFab";

export default {
	components: { Header, NextFab },

	props: ["nextFn"],

	data: () => ({
		strings,
		index: 1,

		steps: [
			{
				title: strings.provision.stepOne,
				error: null,
			},
			{
				title: strings.provision.stepTwo,
				error: null,
			},
			{
				title: strings.provision.stepThree,
				error: null,
			},
		],
	}),

	computed: {
		currentStep() {
			return this.steps[this.index - 1];
		},

		// Does the current step have an error.
		hasError() {
			return !!this.currentStep.error;
		},

		finished() {
			return this.steps.length === this.index - 1;
		},

		loading() {
			return !this.finished && !this.hasError;
		},
	},

	watch: {
		index: {
			handler(val) {
				this.performStep(val);
			},
			immediate: true, // Trigger once immediately on mount
		},
	},

	methods: {
		...mapActions({
			getController: "controller/getModel",
			reprovisionController: "controller/reprovision",
		}),

		async getBridge() {
			let k4 = await this.getController();
			let bridges = k4.model.classes["com.k4connect.zwave.bridge"];
			if (!bridges || bridges.length === 0) {
				throw new Error("No bridge found");
			}
		},

		performStep(i) {
			const stepFn = this[`performStep${i}`];
			if (stepFn) {
				this.$set(this.steps[i - 1], "error", null);
				return stepFn();
			}
		},

		async performStep1() {
			try {
				await this.getBridge();
				this.index++;
			} catch (e) {
				console.error(e);
				this.currentStep.error = strings.provision.connectError;
			}
		},

		async performStep2() {
			try {
				await this.reprovisionController();
				this.index++;
			} catch (e) {
				console.error(e);
				this.currentStep.error = e;
			}
		},

		async performStep3() {
			try {
				await this.getBridge();
				this.index++;
			} catch (e) {
				console.error(e);
				this.currentStep.error = "Controller failed to come back online.";
			}
		},
	},
};
</script>
